// FeaturesSection.jsx
import React from "react";
import PropTypes from "prop-types";
import { Row, Col, Button } from "antd";

/**
 * Alumni Network - Features Section (safe against undefined props)
 * - Uses inline SVG icons
 * - Responsive 3 / 2 / 1 grid
 * - Fade+rise entrance animation with stagger
 */

const DEFAULT_FEATURES = [
  {
    key: "reconnect",
    title: "Reconnect with Classmates",
    desc: "Find and message batchmates across graduating years.",
  },
  {
    key: "events",
    title: "Events & Reunions",
    desc: "RSVP, see event details, and join local meetups.",
  },
  {
    key: "mentorship",
    title: "Mentorship Program",
    desc: "Connect students and young alumni with experienced mentors.",
  },
  {
    key: "jobs",
    title: "Job Board & Opportunities",
    desc: "Discover alumni-posted jobs and internships.",
  },
  {
    key: "stories",
    title: "News & Stories",
    desc: "Read alumni highlights, achievements, and campus updates.",
  },
  {
    key: "donate",
    title: "Give Back & Donate",
    desc: "Support scholarships and campus projects easily.",
  },
];

// Inline SVG icons mapped by key
function getIcons(accentColor) {
  return {
    reconnect: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="3" />
        <circle cx="16" cy="8" r="3" />
        <circle cx="12" cy="16" r="3" />
        <path d="M5 21h14M4 14l4 2m8-2l4 2" />
      </svg>
    ),
    events: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    mentorship: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" />
        <path d="M6.75 18.5a6.97 6.97 0 0110.5 0" />
        <circle cx="18" cy="8" r="2" />
        <path d="M23 20a4 4 0 00-6-3.46" />
        <circle cx="6" cy="8" r="2" />
        <path d="M1 20a4 4 0 016-3.46" />
      </svg>
    ),
    jobs: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
      </svg>
    ),
    stories: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v15H6.5A2.5 2.5 0 0 0 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
    donate: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke={accentColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 21C12 21 4 13.5 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.09C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 13.5 16 21 16 21H12Z" />
      </svg>
    ),
  };
}

export default function FeaturesSection({
  features,
  accentColor = "#6D28D9",
  headline = "What You’ll Find in the Alumni Network",
  subhead = "A community-first platform to reconnect, learn, mentor, and give back.",
  cta = { label: "Explore All Features", href: "/features" },
}) {
  // SAFE fallback: if features is undefined/null, use DEFAULT_FEATURES
  const safeFeatures = Array.isArray(features) && features.length ? features : DEFAULT_FEATURES;
  const ICONS = getIcons(accentColor);

  return (
    <>
      <style>{`
        .features-section {
          padding: 64px 20px 96px;
          text-align: center;
          background: #fff;
          color: #111827;
        }

        .features-inner {
          max-width: 1100px;
          margin: 0 auto;
        }

        .features-headline {
          font-size: 1.875rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: #111827;
        }

        .features-subhead {
          font-size: 1rem;
          color: #4b5563;
          margin-bottom: 48px;
        }

        .feature-card {
          background: #fff;
          border-radius: 10px;
          padding: 28px;
          box-shadow: 0 6px 18px rgba(15,23,42,0.04);
          transition: transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          text-align: left;
          border: 1px solid transparent;
          opacity: 0;
          transform: translateY(14px);
          animation: fadeRise 420ms ease forwards;
        }

        .feature-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 16px 28px rgba(15,23,42,0.08);
          border-color: ${accentColor}22;
        }

        .feature-icon {
          background: ${accentColor}10;
          border-radius: 12px;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
        }

        .feature-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 8px;
        }

        .feature-desc {
          font-size: 0.96rem;
          color: #4b5563;
          margin: 0;
        }

        @keyframes fadeRise {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @media (max-width: 768px) {
          .features-section { padding: 48px 16px; }
          .features-headline { font-size: 1.6rem; }
          .features-subhead { margin-bottom: 32px; }
        }
      `}</style>

      <section className="features-section" aria-labelledby="features-heading">
        <div className="features-inner">
          <h2 id="features-heading" className="features-headline">
            {headline}
          </h2>
          <p className="features-subhead">{subhead}</p>

          <Row gutter={[24, 24]} justify="center" role="list">
            {safeFeatures.map((f, i) => (
              <Col
                key={f.key ?? `${f.title}-${i}`}
                xs={24}
                sm={12}
                lg={8}
                role="listitem"
                style={{
                  animationDelay: `${i * 80}ms`,
                }}
              >
                <article className="feature-card" aria-labelledby={`feature-${i}-title`}>
                  <div className="feature-icon" aria-hidden>
                    {ICONS[f.key] ?? ICONS.reconnect}
                  </div>

                  <h3 id={`feature-${i}-title`} className="feature-title">
                    {f.title}
                  </h3>

                  <p className="feature-desc">{f.desc}</p>
                </article>
              </Col>
            ))}
          </Row>

      
        </div>
      </section>
    </>
  );
}

FeaturesSection.propTypes = {
  features: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      desc: PropTypes.string.isRequired,
    })
  ),
  accentColor: PropTypes.string,
  headline: PropTypes.string,
  subhead: PropTypes.string,
  cta: PropTypes.shape({
    label: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
  }),
};

FeaturesSection.defaultProps = {
  features: DEFAULT_FEATURES,
  accentColor: "#6D28D9",
  headline: "What You’ll Find in the Alumni Network",
  subhead: "A community-first platform to reconnect, learn, mentor, and give back.",
  cta: { label: "Explore All Features", href: "/features" },
};
